import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './productMgmt.css';
import { useUserAuth } from '../../context/UserAuthContext';
import axios from 'axios';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const AddProduct = () => {
    const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
    const { user, language, token, currency } = useUserAuth();
    const merchant_id = user.uid;
    const [loading, setLoading] = useState(false);
    const [merchant, setMerchant] = useState({});
    const [subCategories, setSubCategories] = useState([]);
    const [content, setContent] = useState('');
    const [productImage, setProductImage] = useState([]);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        sub_cat_id: '',
        name: '',
        price: '',
        discount_price: '',
    });

    const percent = formData.price && formData.discount_price
        ? ((formData.price - formData.discount_price) / formData.price * 100).toFixed(1)
        : 0;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const fetchMerchantAndSubcategories = async () => {
        if (!merchant_id) return;
        try {
            setLoading(true);
            const [merchantRes, subCategoriesRes] = await Promise.all([
                axios.get(`${base_url}/api/v1/users/id/${merchant_id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }),
                axios.get(`${base_url}/api/v1/sub-categories`)
            ]);
            setMerchant(merchantRes.data);
            setSubCategories(subCategoriesRes.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load required data');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 10) {
            toast.error('You can upload maximum 10 images');
            return;
        }
        setProductImage(files);
        if (errors.images) {
            setErrors(prev => ({ ...prev, images: '' }));
        }
    };

    const handleImageDelete = (imageToDelete) => {
        setProductImage(prev => prev.filter(img => img !== imageToDelete));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Product name is required';
        // if (!formData.price) newErrors.price = 'Price is required';
        if (
            formData.price &&
            formData.discount_price &&
            Number(formData.discount_price) > Number(formData.price)
        ) {
            newErrors.discount_price = 'Discount must be less than price';
        }
        if (!content.trim()) newErrors.description = 'Description is required';
        if (productImage.length === 0) newErrors.images = 'At least one image is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;
        const calulatedPrice = formData?.price * currency?.country_value;
        const calulatedDiscountPrice = formData?.discount_price ? formData?.discount_price * currency?.country_value : '';
        // return
        try {
            setLoading(true);
            const data = new FormData();
            data.append('id', uuidv4());
            data.append('cat_id', merchant.category_id);
            data.append('sub_cat_id', formData.sub_cat_id || '');
            data.append('name', formData.name);
            if (formData.price) {
                data.append('price', Number(calulatedPrice));
            }

            if (formData.discount_price) {
                data.append('discount_price', Number(calulatedDiscountPrice));
            }
            data.append('product_description', content);
            data.append('merchant_id', merchant_id);
            data.append('stock', true);
            productImage.forEach(file => data.append('image', file));
            console.log('data', data);
            await axios.post(`${base_url}/api/v1/products`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            });

            toast.success('Product added successfully!');
            // Reset form
            setFormData({
                sub_cat_id: '',
                name: '',
                price: '',
                discount_price: '',
            });
            setContent('');
            setProductImage([]);
            setErrors({});
        } catch (err) {
            console.error('Error adding product:', err);
            toast.error(err.response?.data?.message || 'Failed to add product');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (merchant_id) {
            fetchMerchantAndSubcategories();
        }
    }, [merchant_id]);

    useEffect(() => {
        return () => {
            productImage.forEach(file => URL.revokeObjectURL(file));
        };
    }, [productImage]);

    return (
        <div className="page-wrapper">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className='card-header text-white text-center' style={{ backgroundColor: '#085f9a' }}>
                                <h5>{language === 'en' ? 'Add Product/Services' : 'वस्तु या सर्विसेज दर्शाये'}</h5>
                            </div>

                            <div className='card-body'>
                                {loading && (
                                    <div className="overlay">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="product-form">
                                    <div className="mb-3">
                                        <label htmlFor="productName" className="form-label">
                                            Product Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            id="productName"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder='Enter product name'
                                        />
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="price" className="form-label">
                                                Price
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text">{currency?.symbol} </span>
                                                <input
                                                    type="number"
                                                    className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                                                    id="price"
                                                    name="price"
                                                    value={formData.price}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                    step="0.01"
                                                    placeholder='Product price'
                                                />
                                            </div>
                                            {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="discountPrice" className="form-label">
                                                Discount Price
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text">{currency?.symbol}</span>
                                                <input
                                                    type="number"
                                                    className={`form-control ${errors.discount_price ? 'is-invalid' : ''}`}
                                                    id="discountPrice"
                                                    name="discount_price"
                                                    value={formData.discount_price}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                    step="0.01"
                                                    placeholder='Product discount price'
                                                />
                                            </div>
                                            {errors.discount_price && (
                                                <div className="invalid-feedback">{errors.discount_price}</div>
                                            )}
                                        </div>
                                    </div>

                                    {formData.discount_price && formData.price && (
                                        <div className="mb-3 p-3 bg-light rounded">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <span className="fw-bold">{currency?.symbol} {formData.discount_price}</span>
                                                    <span className="text-muted ms-2">
                                                        <del>{currency?.symbol} {formData.price} </del>
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="badge bg-success">
                                                        {percent}% OFF
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Product Description <span className="text-danger">*</span>
                                        </label>
                                        <ReactQuill
                                            theme="snow"
                                            value={content}
                                            onChange={setContent}
                                            placeholder="Describe your product in detail..."
                                            className={`editor-input ${errors.description ? 'border-danger' : ''}`}
                                        />
                                        {errors.description && (
                                            <div className="text-danger small mt-1">{errors.description}</div>
                                        )}
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label">
                                            Product Images <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.images ? 'is-invalid' : ''}`}
                                            onChange={handleImageUpload}
                                            accept="image/*"
                                            multiple
                                            disabled={loading}
                                        />
                                        <div className="form-text">Upload up to 10 images (first image will be used as main)</div>
                                        {errors.images && <div className="invalid-feedback">{errors.images}</div>}

                                        <div className="row mt-3 g-2">
                                            {productImage.length > 0 ? (
                                                productImage.map((file, index) => (
                                                    <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={index}>
                                                        <div className="image-preview-container">
                                                            <img
                                                                src={URL.createObjectURL(file)}
                                                                alt={`Preview ${index}`}
                                                                className="img-thumbnail"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm delete-btn"
                                                                onClick={() => handleImageDelete(file)}
                                                                disabled={loading}
                                                            >
                                                                <MdDelete size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="col-12">
                                                    <div className="text-center py-4 border rounded bg-light">
                                                        <p className="text-muted mb-0">No images selected</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-lg"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Processing...
                                                </>
                                            ) : (
                                                'Add Product'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProduct;